import { useContext, useEffect, useState } from "react";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useArcPlc } from "api-toolkit/useArcPlc";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { BudgetService } from "components/Budget/BudgetService";
import { BUDGET_STATUS_ACTIVE } from "components/Budget/helpers";
import { InsuranceService } from "components/Insurance/InsuranceService";
import {
  MODULE_ARC_PLC,
  MODULE_FINANCIAL,
  MODULE_INSURANCE,
  SECTION_FINANCIAL_SCHEDULE_F,
} from "components/Menu/config/constants";
import { v4 as uuidv4 } from "uuid";

import { CAPITAL_REPLACEMENT_BUFFER_RATE, FinancialService } from "./FinancialService";

export const ERROR_NO_BUDGET_SPECIFIED =
  "No active budget is available.  Please create an active budget and try again.";

// KPI Explanations Dictionary
const KPI_EXPLANATIONS = {
  "Current Ratio": {
    primary: "Shows if you have enough cash and assets you can quickly use to pay your bills coming due soon.",
    secondary: "A value above 1.0 means you should be able to pay your short-term debts without trouble.",
  },
  "Equity-to-Assets Ratio": {
    primary: "Tells you what percentage of your farm you truly own, versus what's financed by loans.",
    secondary: "A higher percentage means you own more of your farm and owe less.",
  },
  "Return on Assets": {
    primary: "Shows how efficiently your farm is making a profit from everything it owns.",
    secondary: "A higher number means you're getting more profit out of your land, equipment, and other assets.",
  },
  "Return on Equity": {
    primary: "Shows how much profit you're earning for every dollar you've personally invested in your farm.",
    secondary: "A higher percentage means your own money is working harder for you.",
  },
  "Debt-to-Assets Ratio": {
    primary: "Shows the percentage of your farm's assets financed by debt.",
    secondary:
      "A lower ratio means less debt and more ownership, while a higher ratio means more borrowing and greater financial risk.",
  },
  "Operating Profit Margin": {
    primary: "Shows what percentage of your farm's revenue is left as profit after covering operating expenses.",
    secondary:
      "A higher margin means your farm is efficiently turning sales into profit; a lower margin means operating costs are taking up most of your income.",
  },
  "Term-Debt Coverage Ratio": {
    primary: "Shows if your farm generates enough income to cover scheduled loan payments.",
    secondary: "A value above 1.0 means you have enough cash flow to pay your term debts as they come due.",
  },
  "Replacement Margin Coverage Ratio": {
    primary: "Measures whether your farm's income covers both loan payments and money needed to replace equipment.",
    secondary:
      "A ratio above 1.0 means you can meet debt payments and still set aside funds for future equipment needs.",
  },
  "Asset-Turnover Rate": {
    primary: "Shows how efficiently your farm turns assets (like land and equipment) into revenue.",
    secondary: "A higher rate means you're generating more sales for every dollar invested in assets.",
  },
  "Operating/Expense Ratio": {
    primary:
      "Shows what portion of your farm's income is used up by operating expenses (excluding interest and depreciation).",
    secondary:
      "A lower ratio means you're keeping more of your income as profit, rather than spending it on day-to-day costs.",
  },
  "Depreciation/Expense Ratio": {
    primary:
      "Shows what percentage of your farm's income goes toward depreciation (the loss in value of equipment and buildings).",
    secondary: "A higher ratio means more of your income is being used to account for wear and tear on assets.",
  },
  "Interest/Expenses Ratio": {
    primary: "Shows what percentage of your farm's income goes to paying interest on loans.",
    secondary: "A lower ratio means less of your income is spent on interest, leaving more for other uses.",
  },
  "Net Income Ratio": {
    primary: "Shows the percentage of your farm's total income that remains as profit after all expenses are paid.",
    secondary:
      "A higher ratio means your farm is more profitable, keeping more of what it earns. Note: Net income is calculated on a pre-tax basis.",
  },
  "Working Capital to Gross Revenue": {
    primary: "Shows how much short-term cash and assets you have compared to the size of your farm's annual income.",
    secondary:
      "A higher ratio (above 25–30%) means you have a strong safety net to cover expenses or downturns; a lower ratio means you may need to borrow to get through the year.",
  },
};

export default function useFinanceReport({
  onError,
  currentYear,

  loadModules,

  // Override api/globalState:
  producerState,
}) {
  const { globalState: contextGlobalState } = useContext(AppContext);
  // Prefer producerState if provided, otherwise use context globalState
  const globalState = producerState || contextGlobalState;

  const [isLoading, setIsLoading] = useState(true);

  // --- --- ---

  const { actions: insuranceActions } = new InsuranceService({
    crop_year: currentYear,
    status: BUDGET_STATUS_ACTIVE,
    apiConfig: globalState,
  });
  const { actions: budgetActions } = new BudgetService({
    apiConfig: globalState,
    year: currentYear,
  });
  const { actions: financialActions } = new FinancialService({
    apiConfig: globalState,
    year: currentYear,
  });

  // --- --- ---

  const [balanceSheet, setBalanceSheet] = useState(null);
  const [isLoadingBalanceSheet, setIsLoadingBalanceSheet] = useState(true);
  const loadBalanceSheet = async () => {
    try {
      setIsLoading(true);
      setIsLoadingBalanceSheet(true);

      const _balanceSheet = await financialActions.getBalanceSheet();
      setBalanceSheet(_balanceSheet);

      setIsLoadingBalanceSheet(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoadingBalanceSheet(false);
      setIsLoading(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (!loadModules?.length || loadModules.includes(MODULE_FINANCIAL)) {
      loadBalanceSheet();
    }
  }, [globalState?.producer_token, currentYear]);

  // --- --- ---

  const [balanceSheetPriorYear, setBalanceSheetPriorYear] = useState(null);
  const [isLoadingBalanceSheetPriorYear, setIsLoadingBalanceSheetPriorYear] = useState(true);
  const loadBalanceSheetPriorYear = async () => {
    try {
      setIsLoading(true);
      setIsLoadingBalanceSheetPriorYear(true);

      const _balanceSheetPriorYear = await financialActions.getBalanceSheetPriorYear();
      setBalanceSheetPriorYear(_balanceSheetPriorYear);

      setIsLoadingBalanceSheetPriorYear(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoadingBalanceSheetPriorYear(false);
      setIsLoading(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (!loadModules?.length || loadModules.includes(MODULE_FINANCIAL)) {
      loadBalanceSheetPriorYear();
    }
  }, [globalState?.producer_token, currentYear]);

  // --- --- ---

  const [incomeStatement, setIncomeStatement] = useState(null);
  const [isLoadingIncomeStatement, setIsLoadingIncomeStatement] = useState(true);
  const loadIncomeStatement = async () => {
    try {
      setIsLoadingIncomeStatement(true);
      setIsLoading(true);

      const _incomeStatement = await financialActions.getIncomeStatement();
      setIncomeStatement(_incomeStatement);

      setIsLoadingIncomeStatement(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoadingIncomeStatement(false);
      setIsLoading(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (!loadModules?.length || loadModules.includes(MODULE_FINANCIAL)) {
      loadIncomeStatement();
    }
  }, [globalState?.producer_token, currentYear]);

  // --- --- ---

  const [budget, setBudget] = useState(null);
  const [isLoadingBudget, setIsLoadingBudget] = useState(true);
  const loadBudget = async () => {
    try {
      setIsLoadingBudget(true);
      setIsLoading(true);

      const budget = await budgetActions.getBudget();
      if (budget) {
        setBudget(budget);
      } else {
        setBudget([]);
        onError && onError(ERROR_NO_BUDGET_SPECIFIED);
        setIsLoadingRatios(false);
      }

      setIsLoadingBudget(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoadingBudget(false);
      setIsLoadingRatios(false);
      setIsLoading(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (
      (!loadModules?.length || loadModules.includes(MODULE_FINANCIAL)) &&
      globalState?.producer_token &&
      currentYear
    ) {
      loadBudget();
    }
  }, [globalState?.producer_token, currentYear]);

  // --- --- ---

  const [producerFarmBaseAcres, setProducerFarmBaseAcres] = useState([]);
  const [arcCoCountyYieldOverrides, setArcCoCountyYieldOverrides] = useState([]);

  const loadModulesSerialized = JSON.stringify(loadModules ?? null);
  const producerFarmBaseAcresKey = JSON.stringify(producerFarmBaseAcres ?? []);
  const arcOverridesKey = JSON.stringify(arcCoCountyYieldOverrides ?? []);

  const arcPlcElectionsHook = useArcPlc({
    baseAcres: producerFarmBaseAcres,
    apiConfig: globalState,
    crop_year: currentYear,
    arcCoCountyYieldOverrides,
  });

  const programElectionsKey = JSON.stringify(arcPlcElectionsHook.programElections ?? []);

  const [arcPlc, setArcPlc] = useState(null);
  const [isLoadingArcPlc, setIsLoadingArcPlc] = useState(true);

  const loadArcPlc = async () => {
    const arcPlcEnabled = !loadModules?.length || loadModules.includes(MODULE_ARC_PLC);
    if (!arcPlcEnabled) {
      setArcPlc(null);
      setProducerFarmBaseAcres((prev) => (prev?.length ? [] : prev));
      setArcCoCountyYieldOverrides((prev) => (prev?.length ? [] : prev));
      setIsLoadingArcPlc(false);
      return;
    }

    if (!globalState?.producer_token || !currentYear) {
      setArcPlc(null);
      setProducerFarmBaseAcres((prev) => (prev?.length ? [] : prev));
      setArcCoCountyYieldOverrides((prev) => (prev?.length ? [] : prev));
      setIsLoadingArcPlc(false);
      return;
    }

    try {
      setIsLoadingArcPlc(true);
      setIsLoading(true);

      const baseAcresFilter = {
        crop_year: currentYear,
        producer_token: globalState.producer_token,
        is_active: true,
      };
      const baseData = await UsersApiHelper.selectProducerFarmBaseAcres(baseAcresFilter, globalState);
      const nextBase = baseData || [];
      setProducerFarmBaseAcres((prev) =>
        JSON.stringify(prev ?? []) === JSON.stringify(nextBase) ? prev : nextBase
      );

      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null,
      });
      let overrides = [];
      try {
        const res = await api.callPost("ArcCoCountyYieldOverride/search", baseAcresFilter, globalState);
        overrides = Array.isArray(res) ? res : [];
      } catch (overrideErr) {
        console.warn(overrideErr);
      }
      setArcCoCountyYieldOverrides((prev) =>
        JSON.stringify(prev ?? []) === JSON.stringify(overrides) ? prev : overrides
      );
    } catch (err) {
      setProducerFarmBaseAcres([]);
      setArcCoCountyYieldOverrides([]);
      setArcPlc(null);
      setIsLoadingArcPlc(false);
      setIsLoading(false);
      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (!loadModules?.length || loadModules.includes(MODULE_ARC_PLC)) {
      loadArcPlc();
    }
  }, [globalState?.producer_token, currentYear, loadModulesSerialized]);

  useEffect(() => {
    const arcPlcEnabled = !loadModules?.length || loadModules.includes(MODULE_ARC_PLC);
    if (!arcPlcEnabled) {
      setArcPlc(null);
      setIsLoadingArcPlc(false);
      return;
    }

    if (!globalState?.producer_token || !currentYear) {
      setIsLoadingArcPlc(false);
      return;
    }

    if (!producerFarmBaseAcres?.length) {
      setArcPlc((prev) => {
        const zero = { totals: { totalPayment: 0, arcPayment: 0, plcPayment: 0 }, arcPlcResponses: [] };
        if (
          prev?.totals?.totalPayment === 0 &&
          prev?.totals?.arcPayment === 0 &&
          prev?.totals?.plcPayment === 0 &&
          !prev?.arcPlcResponses?.length
        ) {
          return prev;
        }
        return zero;
      });
      setIsLoadingArcPlc(false);
      setIsLoading(false);
      return;
    }

    if (arcPlcElectionsHook.isLoadingProgramElections) {
      return;
    }

    if (!arcPlcElectionsHook.programElections?.length) {
      setArcPlc((prev) => {
        const zero = { totals: { totalPayment: 0, arcPayment: 0, plcPayment: 0 }, arcPlcResponses: [] };
        if (
          prev?.totals?.totalPayment === 0 &&
          prev?.totals?.arcPayment === 0 &&
          prev?.totals?.plcPayment === 0 &&
          !prev?.arcPlcResponses?.length
        ) {
          return prev;
        }
        return zero;
      });
      setIsLoadingArcPlc(false);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setIsLoadingArcPlc(true);
      try {
        const paymentsMap = await arcPlcElectionsHook.actions.getArcPlcByElection();
        if (cancelled) return;
        const totalPayment = Object.values(paymentsMap || {}).reduce((acc, v) => acc + (Number(v) || 0), 0);
        const nextArcPlc = {
          totals: {
            totalPayment,
            arcPayment: totalPayment,
            plcPayment: 0,
          },
          arcPlcResponses: Object.entries(paymentsMap || {}).map(([commodity_name, payment]) => ({
            commodity_name,
            arcPayment: payment,
            plcPayment: 0,
            base_acreage: 0,
            type_name: null,
          })),
        };
        setArcPlc((prev) => {
          if (
            prev?.totals?.totalPayment === nextArcPlc.totals.totalPayment &&
            JSON.stringify(prev?.arcPlcResponses) === JSON.stringify(nextArcPlc.arcPlcResponses)
          ) {
            return prev;
          }
          return nextArcPlc;
        });
      } catch (err) {
        if (!cancelled) {
          setArcPlc(null);
          console.warn(err);
          onError && onError(err);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingArcPlc(false);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    loadModulesSerialized,
    globalState?.producer_token,
    currentYear,
    producerFarmBaseAcresKey,
    programElectionsKey,
    arcPlcElectionsHook.isLoadingProgramElections,
    arcOverridesKey,
  ]);

  // --- --- ---

  const [insuranceScenario, setInsuranceScenario] = useState(null);
  const [insuranceScenarioTimestamp, setInsuranceScenarioTimestamp] = useState(uuidv4());
  const [isLoadingInsuranceScenario, setIsLoadingInsuranceScenario] = useState(true);
  const loadInsuranceScenario = async (doSuppressErrors = false) => {
    try {
      setIsLoadingInsuranceScenario(true);
      setInsuranceScenarioTimestamp(uuidv4());

      const _insuranceScenarioPopulated = await insuranceActions.loadInsuranceScenario(doSuppressErrors);

      if (
        _insuranceScenarioPopulated?.length &&
        _insuranceScenarioPopulated[0].budgetCountyPractices?.length &&
        _insuranceScenarioPopulated[0].totals?.insurancePlans?.length
      ) {
        setInsuranceScenario(_insuranceScenarioPopulated[0]);
        setInsuranceScenarioTimestamp(uuidv4());

        insuranceActions
          .calculatePremiums(_insuranceScenarioPopulated[0])
          .then((calculatePremiumsResponseInsuranceScenario) => {
            setInsuranceScenario(calculatePremiumsResponseInsuranceScenario);
            setInsuranceScenarioTimestamp(uuidv4());
            setIsLoadingInsuranceScenario(false);
          });
      } else {
        if (!doSuppressErrors) {
          onError && onError("No insurance plans are selected. Please make your insurance elections.");
        }
        setInsuranceScenario(null);
        setInsuranceScenarioTimestamp(uuidv4());
        setIsLoadingInsuranceScenario(false);
      }
    } catch (err) {
      setIsLoadingInsuranceScenario(false);
      setInsuranceScenario(null);
      setInsuranceScenarioTimestamp(uuidv4());

      setIsLoadingRatios(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token) {
      if (!loadModules?.length || loadModules.includes(MODULE_INSURANCE)) {
        const _doSuppressErrors = (loadModules?.length && !loadModules.includes(MODULE_INSURANCE)) || true;
        loadInsuranceScenario(_doSuppressErrors);
      }
    } else {
      setInsuranceScenario(null);
      setInsuranceScenarioTimestamp(uuidv4());
    }
  }, [globalState?.producer_token, currentYear]);

  // --- --- ---

  const [scheduleF, setScheduleF] = useState(null);
  const [isLoadingScheduleF, setIsLoadingScheduleF] = useState(true);
  const loadScheduleF = async () => {
    try {
      setIsLoading(true);
      setIsLoadingScheduleF(true);

      const _scheduleF = await financialActions.getScheduleF();
      setScheduleF(_scheduleF);

      setIsLoadingScheduleF(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoadingScheduleF(false);
      setIsLoading(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (
      !loadModules?.length ||
      loadModules.includes(MODULE_FINANCIAL) ||
      loadModules.includes(SECTION_FINANCIAL_SCHEDULE_F)
    ) {
      loadScheduleF();
    }
  }, [globalState?.producer_token, currentYear]);

  // --- --- ---

  const [equipmentInventory, setEquipmentInventory] = useState(null);
  const [isLoadingEquipmentInventory, setIsLoadingEquipmentInventory] = useState(true);
  const loadEquipmentInventory = async () => {
    try {
      setIsLoading(true);
      setIsLoadingEquipmentInventory(true);

      const _equipmentInventory = await financialActions.getEquipmentInventory();
      setEquipmentInventory(_equipmentInventory);

      setIsLoadingEquipmentInventory(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoadingEquipmentInventory(false);
      setIsLoading(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (!loadModules?.length || loadModules.includes(MODULE_FINANCIAL)) {
      loadEquipmentInventory();
    }
  }, [globalState?.producer_token, currentYear]);

  // --- --- ---

  const [debtSchedule, setDebtSchedule] = useState(null);
  const [isLoadingDebtSchedule, setIsLoadingDebtSchedule] = useState(true);
  const loadDebtSchedule = async () => {
    try {
      setIsLoading(true);
      setIsLoadingDebtSchedule(true);

      const _debtSchedule = await financialActions.getDebtSchedule();
      setDebtSchedule(_debtSchedule);

      setIsLoadingDebtSchedule(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoadingDebtSchedule(false);
      setIsLoading(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (!loadModules?.length || loadModules.includes(MODULE_FINANCIAL)) {
      loadDebtSchedule();
    }
  }, [globalState?.producer_token, currentYear]);

  // --- --- ---

  const [loanOriginationMemo, setLoanOriginationMemo] = useState(null);
  const [isLoadingLoanOriginationMemo, setIsLoadingLoanOriginationMemo] = useState(true);
  const loadLoanOriginationMemo = async () => {
    try {
      setIsLoading(true);
      // setIsLoadingLoanOriginationMemo(true);

      const _loanOriginationMemo = await financialActions.getLoanOriginationMemo();
      setLoanOriginationMemo(_loanOriginationMemo);

      setIsLoadingLoanOriginationMemo(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoadingLoanOriginationMemo(false);
      setIsLoading(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (!loadModules?.length || loadModules.includes(MODULE_FINANCIAL)) {
      loadLoanOriginationMemo();
    }
  }, [globalState?.producer_token, currentYear]);

  // --- --- ---

  const [isLoadingRatios, setIsLoadingRatios] = useState(true);
  const [ratios, setRatios] = useState(null);

  const loadRatios = () => {
    try {
      setIsLoadingRatios(true);

      const _ratios = financialActions.getRatios({
        budget,
        arcPlc,
        insuranceScenario,
        incomeStatement,
        balanceSheet,
        balanceSheetPriorYear,
      });

      setRatios(_ratios);
      setIsLoadingRatios(false);
    } catch (err) {
      setRatios(null);
      setIsLoadingRatios(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token && budget && arcPlc && insuranceScenario && balanceSheet && incomeStatement) {
      if (!loadModules?.length || loadModules.includes(MODULE_FINANCIAL)) {
        loadRatios();
      }
    } else {
      setRatios(null);
    }
  }, [budget, arcPlc, insuranceScenario, balanceSheet, incomeStatement, globalState?.producer_token]);

  // --- --- ---

  const [debtRatios, setDebtRatios] = useState(null);
  const [isLoadingDebtRatios, setIsLoadingDebtRatios] = useState(true);
  const loadDebtRatios = () => {
    try {
      if (
        balanceSheet &&
        incomeStatement &&
        arcPlc &&
        insuranceScenario &&
        debtSchedule &&
        ratios &&
        loanOriginationMemo
      ) {
        setIsLoading(true);
        setIsLoadingDebtRatios(true);

        const _ratios = financialActions.getDebtRatios({
          balanceSheet,
          incomeStatement,
          arcPlc,
          insuranceScenario,
          debtSchedule,
          loanOriginationMemo,
          financeRatios: ratios,
        });

        setDebtRatios(_ratios);

        setIsLoadingDebtRatios(false);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoadingDebtRatios(false);
      setIsLoading(false);

      console.warn(err);
      onError && onError(err);
    }
  };

  useEffect(() => {
    if (!loadModules?.length || loadModules.includes(MODULE_FINANCIAL)) {
      if (
        balanceSheet &&
        incomeStatement &&
        debtSchedule &&
        arcPlc &&
        insuranceScenario &&
        ratios &&
        loanOriginationMemo
      ) {
        loadDebtRatios();
      }
    }
  }, [
    globalState?.producer_token,
    currentYear,
    balanceSheet,
    incomeStatement,
    arcPlc,
    insuranceScenario,
    debtSchedule,
    ratios,
    loanOriginationMemo,
  ]);

  // --- --- ---

  const getKPICalculations = () => {
    const calculations = {};

    // Current Ratio Calculation
    if (balanceSheet) {
      calculations.currentRatio = {
        name: "Current Ratio",
        value: balanceSheet.current_ratio,
        inputs: [
          { name: "Current Assets", value: balanceSheet.assets_current?.total || 0 },
          { name: "Current Liabilities", value: balanceSheet.liabilities_current?.total || 0 },
        ],
        formula: "Current Assets / Current Liabilities",
        formulaSymbolic: "A / B",
        explanations: KPI_EXPLANATIONS["Current Ratio"],
      };
    }

    // Equity-to-Assets Ratio Calculation
    if (balanceSheet) {
      calculations.equityToAssets = {
        name: "Equity-to-Assets Ratio",
        value: balanceSheet.equity_to_assets_ratio,
        inputs: [
          { name: "Total Equity", value: balanceSheet.equity?.total || 0 },
          { name: "Total Assets", value: balanceSheet.assets?.total || 0 },
        ],
        formula: "Total Equity / Total Assets",
        formulaSymbolic: "A / B",
        explanations: KPI_EXPLANATIONS["Equity-to-Assets Ratio"],
      };
    }

    // Debt-to-Assets Ratio Calculation
    if (balanceSheet) {
      calculations.debtToAssets = {
        name: "Debt-to-Assets Ratio",
        value: balanceSheet.debt_to_assets_ratio,
        inputs: [
          { name: "Total Liabilities", value: balanceSheet.liabilities?.total || 0 },
          { name: "Total Assets", value: balanceSheet.assets?.total || 0 },
        ],
        formula: "Total Liabilities / Total Assets",
        formulaSymbolic: "A / B",
        explanations: KPI_EXPLANATIONS["Debt-to-Assets Ratio"],
      };
    }

    // Return on Assets (ROA) Calculation
    if (ratios && balanceSheet && incomeStatement) {
      calculations.returnOnAssets = {
        name: "Return on Assets",
        value: ratios.return_on_assets_ratio,
        explanations: KPI_EXPLANATIONS["Return on Assets"],
        inputs: [
          { name: "Net Income (pre-tax)", value: ratios.net_income_pre_tax },
          {
            name: "├─ Farm Revenue",
            value: ratios.farm_revenue,
            isSubItem: true,
          },
          {
            name: "├─ All Other Revenue",
            value: ratios.all_other_revenue,
            isSubItem: true,
          },
          {
            name: "├─ Government Program Payments",
            value: ratios.government_program_payments,
            isSubItem: true,
          },
          {
            name: "├─ Production Cost",
            value: -ratios.production_cost,
            isSubItem: true,
          },
          {
            name: "├─ All Other Expenses",
            value: -ratios.all_other_expenses,
            isSubItem: true,
          },
          {
            name: "└─ Taxes (Add-Back)",
            value: ratios.taxes_expense,
            isSubItem: true,
          },
          { name: "Interest Expense", value: incomeStatement?.expenses_interest?.total || 0 },
          { name: "Average Total Assets", value: ratios.average_total_assets || 0 },
          ...(balanceSheetPriorYear
            ? [
                {
                  name: "├─ Current Year Assets",
                  value: balanceSheet?.assets?.total || 0,
                  isSubItem: true,
                },
                {
                  name: "└─ Prior Year Assets",
                  value: balanceSheetPriorYear?.assets?.total || 0,
                  isSubItem: true,
                },
              ]
            : []),
        ],
        formula: "(Net Income + Interest Expense) / Average Total Assets",
        formulaSymbolic: "(A + B) / C",
      };
    }

    // Return on Equity (ROE) Calculation
    if (ratios && balanceSheet && incomeStatement) {
      calculations.returnOnEquity = {
        name: "Return on Equity",
        value: ratios.return_on_equity_ratio,
        explanations: KPI_EXPLANATIONS["Return on Equity"],
        inputs: [
          { name: "Net Income (pre-tax)", value: ratios.net_income_pre_tax },
          { name: "Interest Expense", value: incomeStatement?.expenses_interest?.total || 0 },
          { name: "Average Total Equity", value: ratios.average_total_equity || 0 },
          ...(balanceSheetPriorYear
            ? [
                {
                  name: "├─ Current Year Equity",
                  value: balanceSheet?.equity?.total || 0,
                  isSubItem: true,
                },
                {
                  name: "└─ Prior Year Equity",
                  value: balanceSheetPriorYear?.equity?.total || 0,
                  isSubItem: true,
                },
              ]
            : []),
        ],
        formula: "(Net Income + Interest Expense) / Average Total Equity",
        formulaSymbolic: "(A + B) / C",
      };
    }

    // Operating Profit Margin Calculation
    if (ratios && budget && incomeStatement) {
      const grossRevenue = ratios.gross_revenue;
      const operatingExpenses =
        (budget?.production_cost || 0) +
        ((incomeStatement?.expenses?.total || 0) -
          (incomeStatement?.expenses_interest?.total || 0) -
          (incomeStatement?.expenses_depreciation?.total || 0));
      const operatingIncome = grossRevenue - operatingExpenses;

      calculations.operatingProfitMargin = {
        name: "Operating Profit Margin",
        value: ratios.operating_profit_margin_ratio,
        explanations: KPI_EXPLANATIONS["Operating Profit Margin"],
        inputs: [
          { name: "Operating Income", value: operatingIncome },
          {
            name: "├─ Gross Revenue",
            value: grossRevenue,
            isSubItem: true,
          },
          {
            name: "│  ├─ Farm Revenue",
            value: ratios.farm_revenue || 0,
            isSubItem: true,
          },
          {
            name: "│  ├─ ARC/PLC Payments",
            value: ratios.government_program_payments || 0,
            isSubItem: true,
          },
          {
            name: "│  └─ Other Revenue",
            value: ratios.all_other_revenue || 0,
            isSubItem: true,
          },
          {
            name: "└─ Operating Expenses",
            value: -operatingExpenses,
            isSubItem: true,
          },
          {
            name: "   ├─ Production Cost",
            value: budget?.production_cost || 0,
            isSubItem: true,
          },
          {
            name: "   └─ Other Operating Expenses",
            value:
              (incomeStatement?.expenses?.total || 0) -
              (incomeStatement?.expenses_interest?.total || 0) -
              (incomeStatement?.expenses_depreciation?.total || 0),
            isSubItem: true,
          },
          { name: "Gross Revenue", value: grossRevenue },
        ],
        formula: "Operating Income / Gross Revenue",
        formulaSymbolic: "A / B",
      };
    }

    // Term-Debt Coverage Ratio Calculation
    if (ratios) {
      calculations.termDebtCoverage = {
        name: "Term-Debt Coverage Ratio",
        value: ratios.term_debt_coverage_ratio,
        explanations: KPI_EXPLANATIONS["Term-Debt Coverage Ratio"],
        inputs: [
          { name: "Term Debt Repayment Capacity", value: ratios.term_debt_repayment_capacity || 0 },
          {
            name: "├─ Net Income",
            value: ratios.net_income,
            isSubItem: true,
          },
          {
            name: "│  ├─ Farm Revenue",
            value: ratios.farm_revenue,
            isSubItem: true,
          },
          {
            name: "│  ├─ All Other Revenue",
            value: ratios.all_other_revenue,
            isSubItem: true,
          },
          {
            name: "│  ├─ Government Program Payments",
            value: ratios.government_program_payments,
            isSubItem: true,
          },
          {
            name: "│  ├─ Production Cost",
            value: -ratios.production_cost,
            isSubItem: true,
          },
          {
            name: "│  ├─ All Other Expenses (incl Taxes and Interest)",
            value: -ratios.all_other_expenses,
            isSubItem: true,
          },
          {
            name: "└─ Depreciation (add-back)",
            value: incomeStatement?.expenses_depreciation?.total || 0,
            isSubItem: true,
          },
          {
            name: "Total P & I on Term Debt",
            value: ratios.total_principal_interest_on_term_debt,
          },
          {
            name: "├─ Current Portion of Term Debt",
            value: balanceSheet?.current_portion_of_term_debt_liability || 0,
            isSubItem: true,
          },
          {
            name: "├─ Current Portion of Finance Leases",
            value: balanceSheet?.current_portion_of_finance_leases_liability || 0,
            isSubItem: true,
          },
          {
            name: "├─ Interest on Term Debt",
            value: incomeStatement?.debt_service_expense || 0,
            isSubItem: true,
          },
          {
            name: "└─ Interest on Finance Leases",
            value: incomeStatement?.interest_on_leases_expense || 0,
            isSubItem: true,
          },
        ],
        formula: "Term Debt Repayment Capacity / Total P & I on Term Debt",
        formulaSymbolic: "A / B",
      };
    }

    // Replacement Margin Coverage Ratio Calculation
    if (ratios) {
      calculations.replacementMarginCoverage = {
        name: "Replacement Coverage Ratio",
        value: ratios.replacement_coverage_ratio,
        explanations: KPI_EXPLANATIONS["Replacement Coverage Ratio"],
        inputs: [
          { name: "Repayment Capacity", value: ratios.debt_repayment_capacity || 0 },
          {
            name: "├─ Net Income",
            value: ratios.net_income,
            isSubItem: true,
          },
          {
            name: "│  ├─ Farm Revenue",
            value: ratios.farm_revenue,
            isSubItem: true,
          },
          {
            name: "│  ├─ All Other Revenue",
            value: ratios.all_other_revenue,
            isSubItem: true,
          },
          {
            name: "│  ├─ Government Program Payments",
            value: ratios.government_program_payments,
            isSubItem: true,
          },
          {
            name: "│  ├─ Production Cost",
            value: -ratios.production_cost,
            isSubItem: true,
          },
          {
            name: "│  ├─ All Other Expenses (incl Taxes and Interest)",
            value: -ratios.all_other_expenses,
            isSubItem: true,
          },
          {
            name: "└─ Depreciation (add-back)",
            value: incomeStatement?.expenses_depreciation?.total || 0,
            isSubItem: true,
          },
          {
            name: "└─ Interest expense on current debt (add-back)",
            value: incomeStatement?.expenses_interest?.total || 0,
            isSubItem: true,
          },
          {
            name: "Uses of Repayment and Replacement Capacity",
            value: ratios.uses_of_repayment_replacement_capacity || 0,
          },
          {
            name: "├─ Current Portion of Term Debt",
            value: balanceSheet?.current_portion_of_term_debt_liability || 0,
            isSubItem: true,
          },
          {
            name: "├─ Current Portion of Finance Leases",
            value: balanceSheet?.current_portion_of_finance_leases_liability || 0,
            isSubItem: true,
          },
          {
            name: "├─ Interest on Term Debt",
            value: incomeStatement?.debt_service_expense || 0,
            isSubItem: true,
          },
          {
            name: "└─ Interest on Finance Leases",
            value: incomeStatement?.interest_on_leases_expense || 0,
            isSubItem: true,
          },
          {
            name: "├─ Interest expense on current debt (add-back)",
            value: incomeStatement?.expenses_interest?.total || 0,
            isSubItem: true,
          },
          {
            name: "├─ Loss carryover",
            value: balanceSheet?.loss_carryover_asset || 0,
            isSubItem: true,
          },
          {
            name: "├─ Payment on personal liabilities",
            value: incomeStatement?.expenses_debt_service?.total - incomeStatement?.debt_service_expense || 0,
            isSubItem: true,
          },
          { name: "Replacement Allowance", value: ratios.replacement_allowance || 0 },
          {
            name: "├─ Base Depreciation Expense",
            value: incomeStatement?.expenses_depreciation?.total || 0,
            isSubItem: true,
          },
          {
            name: `└─ Buffer Rate (${((CAPITAL_REPLACEMENT_BUFFER_RATE - 1) * 100).toFixed(0)}%)`,
            value: ratios.replacement_allowance - (incomeStatement?.expenses_depreciation?.total || 0),
            isSubItem: true,
          },
        ],
        formula: "Repayment Capacity / (Uses of Repayment and Replacement Capacity + Replacement Allowance)",
        formulaSymbolic: "A / B",
      };
    }

    // Asset-Turnover Rate Calculation
    if (ratios && budget && incomeStatement) {
      const grossRevenue = ratios.gross_revenue;
      calculations.assetTurnover = {
        name: "Asset-Turnover Rate",
        value: ratios.asset_turnover_ratio,
        explanations: KPI_EXPLANATIONS["Asset-Turnover Rate"],
        inputs: [
          { name: "Gross Revenue", value: grossRevenue },
          {
            name: "├─ Farm Revenue",
            value: ratios.farm_revenue || 0,
            isSubItem: true,
          },
          {
            name: "├─ ARC/PLC Payments",
            value: ratios.government_program_payments || 0,
            isSubItem: true,
          },
          {
            name: "└─ Other Revenue",
            value: ratios.all_other_revenue || 0,
            isSubItem: true,
          },
          { name: "Average Total Assets", value: ratios.average_total_assets || 0 },
          ...(balanceSheetPriorYear
            ? [
                {
                  name: "├─ Current Year Assets",
                  value: balanceSheet?.assets?.total || 0,
                  isSubItem: true,
                },
                {
                  name: "└─ Prior Year Assets",
                  value: balanceSheetPriorYear?.assets?.total || 0,
                  isSubItem: true,
                },
              ]
            : []),
        ],
        formula: "Total Revenue / Average Total Assets",
        formulaSymbolic: "A / B",
      };
    }

    // Operating/Expense Ratio Calculation
    if (ratios && budget && incomeStatement) {
      const grossRevenue = ratios.gross_revenue;
      const operatingExpenses =
        (budget?.production_cost || 0) +
        ((incomeStatement?.expenses?.total || 0) -
          (incomeStatement?.expenses_interest?.total || 0) -
          (incomeStatement?.expenses_depreciation?.total || 0));

      calculations.operatingExpenseRatio = {
        name: "Operating/Expense Ratio",
        value: ratios.operating_expense_ratio,
        explanations: KPI_EXPLANATIONS["Operating/Expense Ratio"],
        inputs: [
          { name: "Operating Expenses", value: operatingExpenses },
          {
            name: "├─ Production Cost",
            value: budget?.production_cost || 0,
            isSubItem: true,
          },
          {
            name: "├─ Total Other Expenses",
            value: incomeStatement?.expenses?.total || 0,
            isSubItem: true,
          },
          {
            name: "├─ Less: Interest Expense",
            value: -(incomeStatement?.expenses_interest?.total || 0),
            isSubItem: true,
          },
          {
            name: "└─ Less: Depreciation Expense",
            value: -(incomeStatement?.expenses_depreciation?.total || 0),
            isSubItem: true,
          },
          { name: "Gross Revenue", value: grossRevenue },
          {
            name: "├─ Farm Revenue",
            value: ratios.farm_revenue || 0,
            isSubItem: true,
          },
          {
            name: "├─ ARC/PLC Payments",
            value: ratios.government_program_payments || 0,
            isSubItem: true,
          },
          {
            name: "└─ Other Revenue",
            value: ratios.all_other_revenue || 0,
            isSubItem: true,
          },
        ],
        formula: "(Production Cost + Other Expenses - Interest - Depreciation) / Gross Revenue",
        formulaSymbolic: "A / B",
      };
    }

    // Depreciation/Expense Ratio Calculation
    if (ratios && budget && incomeStatement) {
      const grossRevenue = ratios.gross_revenue;
      calculations.depreciationExpenseRatio = {
        name: "Depreciation/Expense Ratio",
        value: ratios.depreciation_expense_ratio,
        explanations: KPI_EXPLANATIONS["Depreciation/Expense Ratio"],
        inputs: [
          { name: "Depreciation Expense", value: incomeStatement?.expenses_depreciation?.total || 0 },
          { name: "Gross Revenue", value: grossRevenue },
        ],
        formula: "Depreciation Expense / Gross Revenue",
        formulaSymbolic: "A / B",
      };
    }

    // Interest/Expenses Ratio Calculation
    if (ratios && budget && incomeStatement) {
      const grossRevenue = ratios.gross_revenue;
      calculations.interestExpenseRatio = {
        name: "Interest/Expenses Ratio",
        value: ratios.interest_expense_ratio,
        explanations: KPI_EXPLANATIONS["Interest/Expenses Ratio"],
        inputs: [
          { name: "Interest Expense", value: incomeStatement?.expenses_interest?.total || 0 },
          { name: "Gross Revenue", value: grossRevenue },
        ],
        formula: "Interest Expense / Gross Revenue",
        formulaSymbolic: "A / B",
      };
    }

    // Net Income Ratio Calculation
    if (ratios && budget && incomeStatement) {
      const grossRevenue = ratios.gross_revenue;

      calculations.netIncomeRatio = {
        name: "Net Income Ratio",
        value: ratios.income_ratio,
        explanations: KPI_EXPLANATIONS["Net Income Ratio"],
        inputs: [
          { name: "Net Income (pre-tax)", value: ratios.net_income_pre_tax },
          {
            name: "├─ Farm Revenue",
            value: ratios.farm_revenue,
            isSubItem: true,
          },
          {
            name: "├─ All Other Revenue",
            value: ratios.all_other_revenue,
            isSubItem: true,
          },
          {
            name: "├─ Government Program Payments",
            value: ratios.government_program_payments,
            isSubItem: true,
          },
          {
            name: "├─ Production Cost",
            value: -ratios.production_cost,
            isSubItem: true,
          },
          {
            name: "├─ All Other Expenses",
            value: -ratios.all_other_expenses,
            isSubItem: true,
          },
          {
            name: "└─ Taxes (Add-Back)",
            value: ratios.taxes_expense,
            isSubItem: true,
          },
          { name: "Gross Revenue", value: grossRevenue },
        ],
        formula: "Net Income / Gross Revenue",
        formulaSymbolic: "A / B",
      };
    }

    // Working Capital to Gross Revenue Calculation
    if (ratios && balanceSheet && budget && incomeStatement && arcPlc) {
      const grossRevenue = ratios.gross_revenue;
      calculations.workingCapitalToGrossRevenue = {
        name: "Working Capital to Gross Revenue",
        value: ratios.working_capital_to_gross_revenue_ratio,
        explanations: KPI_EXPLANATIONS["Working Capital to Gross Revenue"],
        inputs: [
          { name: "Working Capital", value: balanceSheet?.working_capital || 0 },
          {
            name: "├─ Current Assets",
            value: balanceSheet?.assets_current?.total || 0,
            isSubItem: true,
          },
          {
            name: "└─ Current Liabilities",
            value: -(balanceSheet?.liabilities_current?.total || 0),
            isSubItem: true,
          },
          { name: "Gross Revenue", value: grossRevenue },
          {
            name: "├─ Farm Revenue",
            value: ratios.farm_revenue || 0,
            isSubItem: true,
          },
          {
            name: "├─ ARC/PLC Payments",
            value: ratios.government_program_payments || 0,
            isSubItem: true,
          },
          {
            name: "└─ Other Revenue",
            value: ratios.all_other_revenue || 0,
            isSubItem: true,
          },
        ],
        formula: "Working Capital / Gross Revenue",
        formulaSymbolic: "A / B",
      };
    }

    return calculations;
  };

  // --- --- ---

  return {
    isLoading,

    isLoadingBudget,
    budget,
    loadBudget,

    arcPlc,
    isLoadingArcPlc,
    loadArcPlc,

    isLoadingInsuranceScenario,
    insuranceScenario,
    loadInsuranceScenario,

    balanceSheet,
    isLoadingBalanceSheet,
    loadBalanceSheet,
    setBalanceSheet,

    balanceSheetPriorYear,
    isLoadingBalanceSheetPriorYear,
    loadBalanceSheetPriorYear,

    incomeStatement,
    isLoadingIncomeStatement,
    loadIncomeStatement,

    scheduleF,
    isLoadingScheduleF,
    loadScheduleF,

    equipmentInventory,
    isLoadingEquipmentInventory,
    loadEquipmentInventory,

    debtSchedule,
    isLoadingDebtSchedule,
    loadDebtSchedule,

    loanOriginationMemo,
    isLoadingLoanOriginationMemo,
    loadLoanOriginationMemo,

    debtRatios,
    isLoadingDebtRatios,
    loadDebtRatios,

    isLoadingRatios,
    ratios,
    loadRatios,

    getKPICalculations,
  };
}
